import { v4 as uuidV4 } from 'uuid';
import { isNative } from '@origin-digital/platform-helpers';
import { isOnServer } from '@origin-digital/tal-common';
import { LOG_TRACE_HEADER } from './logging';

const STORAGE_KEY = 'odSales:businessTxId';

// Cross-bundle TTL window for the RN-WebView path. localStorage outlives
// WebView remounts, which is why we use it — but we need a journey boundary
// so a stale id doesn't leak across days. Checked at module load only;
// reused across TAL bundles whenever consecutive boots fall within the window.
const RN_TTL_MS = 30 * 60 * 1000;

type RnCached = { id: string; ts: number };

const readSession = (): string | null => sessionStorage.getItem(STORAGE_KEY);
const writeSession = (id: string) => sessionStorage.setItem(STORAGE_KEY, id);

const readLocalRn = (): RnCached | null => {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as RnCached;
    if (!parsed?.id || typeof parsed.ts !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeLocalRn = (id: string, ts: number) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ id, ts }));
};

/**
 * Generates a single traceId per journey, persisted across TAL app boundaries
 * so cross-app journeys share one id in New Relic.
 *
 * Browser → sessionStorage (per-tab, survives cross-TAL nav and refresh).
 *
 * RN WebView → localStorage. Survives WebView remount (localStorage is
 * per-app-process, not per-WebView). TTL is checked once at module load:
 * if the stored entry is fresh, the id is reused and its timestamp refreshed
 * for the next boot; if it's older than RN_TTL_MS, a new id is generated.
 * Each boot extends the window by another RN_TTL_MS, so an active journey
 * (frequent cross-bundle navs) stays on one id; a >30-min gap between boots
 * starts a new one.
 */
const createTraceIdGenerator = () => {
  let txId: string | undefined;
  return () => {
    if (isOnServer()) return uuidV4();
    if (txId) return txId;
    try {
      const useRn = isNative();
      const now = Date.now();
      if (useRn) {
        const stored = readLocalRn();
        const fresh = stored && now - stored.ts <= RN_TTL_MS;
        const nextTxId = fresh ? stored.id : uuidV4();
        writeLocalRn(nextTxId, now);
        txId = nextTxId;
      } else {
        const nextTxId = readSession() ?? uuidV4();
        writeSession(nextTxId);
        txId = nextTxId;
      }
      return txId;
    } catch {
      return uuidV4();
    }
  };
};

const getSessionTraceId = createTraceIdGenerator();

/**
 * Returns x-od-business-tx-id header as per Log Tracing Standards standard
 * @returns x-od-business-tx-id
 */
export const getHeaderBusinessTnxId = () => ({
  [LOG_TRACE_HEADER.BUSINESS_TX_ID]: getSessionTraceId(),
});
/**
 * Returns x-od-trace-id header as per Log Tracing Standards standard
 * Returns a fresh uuid every call.
 * @returns x-od-trace-id
 */
export const getHeaderTraceId = () => ({ [LOG_TRACE_HEADER.TRACE_ID]: uuidV4() });

/**
 * Combines business transaction and trace headers
 * @returns Combined headers object
 */
export const getTraceHeaders = () =>
  Object.assign({}, getHeaderBusinessTnxId(), getHeaderTraceId());

export { getSessionTraceId };
